// ============================================================================
// atendimentoApi · IA de atendimento, modo teste (13/10/2026)
// ============================================================================
// Duas portas no servidor, ambas com x-session-token:
//   /api/atendimento-fila      fila da recepção (pré-agendamentos e "cliente
//                              pede a recepção"), escopo por cargo/unidade
//   /api/atendimento-conversa  simulador do master (conversa com a assistente)
// REGRA LINEAR: falhou → lança Error com a mensagem do servidor e o status
// (a tela mantém o estado anterior e avisa). Nunca "lista vazia" calada.
// Também moram aqui os formatos usados pelas telas (telefone, valor, datas).
// 17/10/2026 (etapa E1): ações dos cartões novos (concluído, sem encaixe,
// outro horário), nota interna, transferir, contador do menu e o roteiro v2
// no Simulador.
// 17/10/2026 (etapa E2): pesquisa de satisfação na conversa no Simulador
// (simular, toque em botão/lista, prazo, comandas de hoje, gatilho em sombra).
// 25/10/2026 (rajada da Sofia): nas conversas em que o servidor liga a rajada
// (op=conversa devolve rajada: true), cada envio leva idMensagem (o servidor
// grava 'sim:<id>' e a mesma mensagem mandada de novo não duplica) e a ordem
// em que o master digitou. Sem fetch com prazo: o servidor pode segurar a
// requisição até 150 s esperando o cliente parar de digitar.
// ============================================================================

import { sessionToken } from './secureRest.js';

const FILA = '/api/atendimento-fila';
const CONVERSA = '/api/atendimento-conversa';

async function call(base, method, params = {}) {
  const token = sessionToken();
  if (!token) { const e = new Error('Sessão expirada. Entre de novo.'); e.status = 401; throw e; }
  const limpos = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
  const url = method === 'GET' ? `${base}?${new URLSearchParams(limpos)}` : base;
  let r;
  try {
    r = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'x-session-token': token },
      body: method === 'GET' ? undefined : JSON.stringify(params),
      cache: 'no-store',
    });
  } catch {
    const e = new Error('Sem conexão com o servidor. Tente de novo em instantes.');
    e.status = 0;
    throw e;
  }
  const text = await r.text().catch(() => '');
  const trimmed = text.trim();
  let j = null;
  if (trimmed && !trimmed.startsWith('<')) {
    try { j = JSON.parse(trimmed); } catch { j = null; }
  }
  if (!r.ok) {
    const e = new Error((j && j.error) || `Erro ${r.status} ao falar com o servidor.`);
    e.status = r.status;
    e.dados = j;
    throw e;
  }
  if (!j || typeof j !== 'object') {
    const e = new Error('Resposta inesperada do servidor (tente de novo).');
    e.status = r.status;
    throw e;
  }
  return j;
}

// ─── Fila da recepção ─────────────────────────────────────────────────────

export const filaApi = {
  me: () => call(FILA, 'GET', { op: 'me' }),
  lista: ({ unitId, grupo } = {}) => call(FILA, 'GET', { op: 'lista', unitId, grupo }),
  item: (id) => call(FILA, 'GET', { op: 'item', id }),
  catalogo: (unitId, { soProfissionais = false } = {}) =>
    call(FILA, 'GET', { op: 'catalogo', unitId, so: soProfissionais ? 'profissionais' : undefined }),
  assumir: (id) => call(FILA, 'POST', { op: 'assumir', id }),
  agendei: (id, { final, nota, combinadoComCliente } = {}) => call(FILA, 'POST', { op: 'agendei', id, final, nota, combinadoComCliente }),
  naoDeu: (id, { motivo, nota } = {}) => call(FILA, 'POST', { op: 'nao_deu', id, motivo, nota }),
  resolvido: (id, { devolverIa = true, nota } = {}) => call(FILA, 'POST', { op: 'resolvido', id, devolverIa, nota }),
  reabrir: (id) => call(FILA, 'POST', { op: 'reabrir', id }),
  // cartões de remarcação, cancelamento, encaixe (17/10/2026)
  concluido: (id, { final, nota, combinadoComCliente } = {}) => call(FILA, 'POST', { op: 'concluido', id, final, nota, combinadoComCliente }),
  semEncaixe: (id, { nota } = {}) => call(FILA, 'POST', { op: 'sem_encaixe', id, nota }),
  oferecerOutro: (id, { opcoes, nota } = {}) => call(FILA, 'POST', { op: 'oferecer_outro', id, opcoes, nota }),
  nota: (id, { texto } = {}) => call(FILA, 'POST', { op: 'nota', id, texto }),
  transferir: (id, { unitId, motivo } = {}) => call(FILA, 'POST', { op: 'transferir', id, unitId, motivo }),
  // Sofia v3 (24/10/2026): "Ainda estou confirmando..." uma vez por cartão
  avisarDemora: (id) => call(FILA, 'POST', { op: 'avisar_demora', id }),
  contagem: () => call(FILA, 'GET', { op: 'contagem' }),
};

// ─── Simulador (só master) ────────────────────────────────────────────────

export const conversaApi = {
  lista: () => call(CONVERSA, 'GET', { op: 'lista' }),
  abrir: (id) => call(CONVERSA, 'GET', { op: 'conversa', id }),
  // 24/10/2026, noite: número único. modo 'rede' (sem unitId; a unidade de
  // costume simulada só vale com o nome simulado) ou o número de uma unidade.
  nova: ({ unitId, modo, unidadeCostume, telefone, modelo, notificarRecepcao = false, promptVersao, perfilSimulado }) =>
    call(CONVERSA, 'POST', modo === 'rede'
      ? { op: 'nova', modo: 'rede', unidadeCostume, telefone, modelo, notificarRecepcao, promptVersao, perfilSimulado }
      : { op: 'nova', unitId, telefone, modelo, notificarRecepcao, promptVersao, perfilSimulado }),
  // retomada da Consultoria Visagista no Simulador: roda a etapa do dia 1, 3, 7 ou 15 agora
  retomadaSimular: (conversaId, dias) => call(CONVERSA, 'POST', { op: 'retomada_simular', conversaId, dias }),
  // extra = { idMensagem, ordem } só na rajada (25/10/2026); sem eles o corpo é o de sempre (o JSON tira o undefined)
  mensagem: (conversaId, texto, resposta, extra) =>
    call(CONVERSA, 'POST', { op: 'mensagem', conversaId, texto, resposta, idMensagem: extra?.idMensagem, ordem: extra?.ordem }),
  recepcaoResponde: (conversaId, texto) => call(CONVERSA, 'POST', { op: 'recepcao_responde', conversaId, texto }),
  devolverIa: (conversaId) => call(CONVERSA, 'POST', { op: 'devolver_ia', conversaId }),
  encerrar: (conversaId) => call(CONVERSA, 'POST', { op: 'encerrar', conversaId }),
  horarios: ({ unitId, dia, servicos = [], profissionalId }) =>
    call(CONVERSA, 'GET', { op: 'horarios', unitId, dia, servicos: servicos.join(','), profissionalId }),
  uso: () => call(CONVERSA, 'GET', { op: 'uso' }),
  // pesquisa de satisfação na conversa (17/10/2026)
  simularPesquisa: ({ unitId, telefone, modelo, promptVersao, visita }) =>
    call(CONVERSA, 'POST', { op: 'pesquisa_simular', unitId, telefone, modelo, promptVersao, visita }),
  vencerPesquisa: (conversaId, horas) => call(CONVERSA, 'POST', { op: 'pesquisa_vencer', conversaId, horas }),
  comandasHoje: (unitId) => call(CONVERSA, 'GET', { op: 'comandas_hoje', unitId }),
  sombraPesquisa: (unitId) => call(CONVERSA, 'GET', { op: 'pesquisa_sombra', unitId }),
};

// ─── Constantes da tela ───────────────────────────────────────────────────

export const UNIDADES_ATD = ['unit-moema', 'unit-vila', 'unit-itaim', 'unit-oscar', 'unit-jardins'];
export const NOME_CURTO = {
  'unit-moema': 'Moema', 'unit-vila': 'Vila Olímpia', 'unit-itaim': 'Itaim Bibi', 'unit-oscar': 'Oscar Freire', 'unit-jardins': 'Jardins',
};

// Número único (24/10/2026, noite): a conversa de rede e quem definiu a
// unidade (as fontes de api/_lib/atendimento-marcas.js), em palavras.
export const REDE = 'rede';
export const FONTE_UNIDADE = Object.freeze({
  cliente: 'escolhida pelo cliente',
  sugestao_aceita: 'a de costume, aceita pelo cliente',
  profissional: 'onde atende o profissional que ele pediu',
  pedido: 'a do pedido',
  horario_existente: 'a do horário que ele já tem',
  equipe: 'transferida pela equipe',
  costume: 'a de costume, ainda não confirmada',
  plantao: 'plantão, ainda não escolhida',
});
// Retomada da Consultoria Visagista: etapas e por que parou (códigos de estadoRetomada).
export const ETAPA_RETOMADA = Object.freeze({ d1: 'dia 1', d3: 'dia 3', d7: 'dia 7', d15: 'dia 15' });
export const PARADA_RETOMADA = Object.freeze({
  nao_visagismo: 'a conversa não é da Consultoria Visagista',
  sem_resposta_da_sofia: 'a Sofia ainda não respondeu na consultoria',
  encerrada: 'a conversa foi encerrada',
  equipe: 'a conversa está com a equipe',
  pedido: 'já há um pedido de agendamento',
  pediu_parar: 'o cliente pediu para não receber mais mensagens',
  recusou: 'o cliente disse que não tem interesse',
  respondeu: 'o cliente respondeu',
  ultima_do_cliente: 'a última mensagem é do cliente',
  concluida: 'as 4 etapas já saíram',
  cedo: 'ainda não é o dia da próxima etapa',
  perdida: 'passou do prazo da etapa',
  fora_da_janela: 'fora da janela (segunda a sábado, das 10h às 19h, sem feriado)',
  canal: 'o canal não está liberado para a retomada',
  sem_modelo_meta: 'falta o modelo aprovado pela Meta',
});

// Modelos que o simulador oferece (o servidor confere a lista e prende o
// modelo na conversa).
export const MODELOS = [
  { id: 'claude-opus-5-5', nome: 'Opus 5.5' },
  { id: 'claude-sonnet-5-5', nome: 'Sonnet 5.5' },
  { id: 'claude-haiku-5-5', nome: 'Haiku 5.5' },
];
// Só até o servidor responder (op=lista traz modeloPadrao, que segue IA_ATENDENTE_MODEL).
export const MODELO_PADRAO = 'claude-haiku-5-5';
export const nomeModelo = (id) => MODELOS.find(m => m.id === id)?.nome || id || '';

// Roteiros do Simulador (o servidor manda a lista e o padrão: a Sofia, roteiro
// v3, quando IA_ROTEIRO_V3 e IA_PEDIDOS estão ligados; senão a v1). Este valor
// só vale até o servidor responder.
export const VERSAO_PADRAO = 'fk-atd-v1';
export const VERSAO_SOFIA = 'fk-atd-v3';
export const nomeVersao = (id) => (id === 'fk-atd-v3' ? 'Sofia (v3)' : id === 'fk-atd-v2' ? 'Roteiro v2' : 'Roteiro v1');

export const TEXTO_MAX = 1500;

// ─── Rajada da Sofia (25/10/2026) ─────────────────────────────────────────
// O mesmo formato que o servidor aceita em b.idMensagem.
export const RE_ID_MENSAGEM = /^[A-Za-z0-9-]{8,64}$/;

/**
 * Id de um envio do Simulador. crypto.randomUUID quando existe (Safari antigo
 * e página sem https não têm); senão 32 hex do getRandomValues; por último,
 * relógio + Math.random (é só para a chegada repetida não duplicar).
 */
export function novoIdMensagem(cr = globalThis.crypto) {
  try {
    if (cr && typeof cr.randomUUID === 'function') {
      const id = String(cr.randomUUID());
      if (RE_ID_MENSAGEM.test(id)) return id;
    }
  } catch { /* segue para a troca */ }
  try {
    if (cr && typeof cr.getRandomValues === 'function') {
      const b = cr.getRandomValues(new Uint8Array(16));
      const id = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
      if (RE_ID_MENSAGEM.test(id)) return id;
    }
  } catch { /* segue para a troca */ }
  let id = Date.now().toString(36);
  while (id.length < 32) id += Math.floor(Math.random() * 2176782336).toString(36);
  return id.slice(0, 32);
}

/** Ordem de envio por conversa: o relógio, e sempre maior que a anterior (inteiro seguro > 0). */
export function proximaOrdem(ultima, agora = Date.now()) {
  const antes = Number(ultima);
  const base = Number.isSafeInteger(antes) && antes > 0 ? antes : 0;
  const t = Math.floor(Number(agora));
  return Math.max(Number.isSafeInteger(t) && t > 0 ? t : 1, base + 1);
}

/** 'sim:<idMensagem>' (novas[].idExterno) → idMensagem; outro formato → null. */
export function idMensagemDoExterno(idExterno) {
  if (typeof idExterno !== 'string' || !idExterno.startsWith('sim:')) return null;
  const id = idExterno.slice(4);
  return RE_ID_MENSAGEM.test(id) ? id : null;
}

// ─── Formatos ─────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, '0');

/** Só dígitos, sem o 55 do país quando vier com ele (11 dígitos BR). */
export function digitosTelefone(v) {
  let d = String(v || '').replace(/\D/g, '');
  if (d.length > 11 && d.startsWith('55')) d = d.slice(2);
  return d.slice(0, 11);
}

/** "(11) 98765-4321" enquanto digita. */
export function mascaraTelefone(v) {
  const d = digitosTelefone(v);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export const telefoneCompleto = (v) => digitosTelefone(v).length >= 10;

/** Link do WhatsApp (só no canal whatsapp; no teste a tela não mostra link). */
export function linkWhatsApp(tel) {
  let d = String(tel || '').replace(/\D/g, '');
  if (!d) return null;
  if (!d.startsWith('55')) d = `55${d}`;
  return `https://wa.me/${d}`;
}

// 28/10/2026: no WhatsApp o número do cartão sempre tem o código do país (o brasileiro com 55, o
// estrangeiro como veio do wa_id). Estrangeiro = 10 a 15 dígitos que não começam com 55. → dígitos | null
export function telefoneEstrangeiro(tel) {
  const d = String(tel || '').replace(/\D/g, '');
  return d.length >= 10 && d.length <= 15 && !d.startsWith('55') ? d : null;
}
/** "+1 415 555 1234", "+351912345678": o número estrangeiro com o código do país. */
export function mascaraInternacional(d) {
  return /^1\d{10}$/.test(d) ? `+1 ${d.slice(1, 4)} ${d.slice(4, 7)} ${d.slice(7)}` : `+${d}`;
}

export function reais(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: n % 1 ? 2 : 0 });
}

export function dolares(v, casas = 4) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 'US$ 0';
  return `US$ ${n.toFixed(casas).replace('.', ',')}`;
}

/** "agora", "há 12 min", "há 3 h", "ontem", "há 4 dias", "28/09". */
export function haQuanto(iso, agora = Date.now()) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const min = Math.floor((agora - d.getTime()) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const hoje = new Date(agora); hoje.setHours(0, 0, 0, 0);
  const dia = new Date(d); dia.setHours(0, 0, 0, 0);
  const dias = Math.round((hoje - dia) / 86400000);
  if (dias === 0) return `há ${Math.floor(min / 60)} h`;
  if (dias === 1) return 'ontem';
  if (dias < 7) return `há ${dias} dias`;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
}

/** "13/10 às 09:12" */
export function diaHora(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} às ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "09:12" */
export function hora(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const DIAS = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];

/** 'AAAA-MM-DD' → "quinta-feira, 15/10" */
export function diaPorExtenso(dia) {
  if (typeof dia !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(dia)) return '';
  const d = new Date(`${dia.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  return `${DIAS[d.getUTCDay()]}, ${dia.slice(8, 10)}/${dia.slice(5, 7)}`;
}

/** Hoje em São Paulo, 'AAAA-MM-DD'. */
export function hojeSP(date = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}
